import * as React from 'react';
import { useBoolean, useStateCallback, isDefined, useStyles, error, isFunction, noop, useKeyPress, getNextIndex, getPrevIndex } from '@sravni/react-utils';
import { Button } from '../../Button/src/Button.js';
import { Menu } from '../../Menu/index.js';
import { Popover } from '../../Popover/src/Popover.js';
import '../../Typography/src/Heading.js';
import '../../Typography/src/Link.js';
import '../../Typography/src/ListItem.js';
import '../../Typography/src/OrderedList.js';
import { Text } from '../../Typography/src/Text.js';
import '../../Typography/src/UnorderedList.js';
import modules_c052660e from '../styles/Dropdown.module.less.js';

// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-statements */
const defaultEmptyContentText = 'К сожалению, не получилось загрузить список, скоро всё поправим';
const minWidthModifier = {
    name: 'minWidth',
    enabled: true,
    phase: 'beforeWrite',
    requires: ['computeStyles'],
    fn: ({ state }) => {
        state.styles.popper.minWidth = '280px';
    },
};
/** Helper function, return object with positioner settings */
const makePositionerSettings = (placement, matchWidth, flip) => ({
    placement,
    modifiers: matchWidth ? [] : [minWidthModifier],
    matchWidth,
    flip,
});
/**
 * @description Dropdown component to select value from options
 *
 * @component
 * @example
 * ```jsx
 * <Dropdown options={options}>{triggerElement}</Dropdown>
 * ```
 */
const Dropdown = React.forwardRef((props, ref) => {
    const { defaultValue, value, placement = 'bottom-start', mode, mobile, fullscreen = true, matchWidth = false, options, disabled, readonly, header, emptyContent, optionsSort = true, className, children, closeOnSelect = true, flip = true, onClick, onChange, onOpen, onClose, ...restProps } = props;
    const isFirstRender = React.useRef(true);
    const [visible, setVisible] = useBoolean(false);
    const focused = React.useRef(false);
    const [innerValue, setInnerValue] = useStateCallback(isDefined(value) ? value : defaultValue);
    const [innerOptions, setInnerOptions] = React.useState(options || []);
    const [focusedIndex, setFocusedIndex] = React.useState(-1);
    const isMultiple = mode === 'multiple';
    const cx = useStyles(modules_c052660e);
    const popoverClassNames = cx(className, 'container', {
        dropdown: !mobile,
    });
    let triggerElement = null;
    // Ensure Dropdown has only one child node
    const count = React.useMemo(() => React.Children.count(children), [children]);
    const condition = count === 0 || count > 1;
    if (condition) {
        error({
            condition,
            message: 'Ensure Dropdown has only one child node',
        })();
        triggerElement = children;
    }
    else {
        const child = React.Children.only(children);
        const handleClickTrigger = (event) => {
            if (disabled)
                return;
            !readonly && setVisible.toggle();
            focused.current = true;
            child.props.onClick?.(event);
        };
        const handleFocusBlurTrigger = (event) => {
            if (event.type === 'focus') {
                focused.current = true;
                child.props.onFocus?.(event);
            }
            else {
                focused.current = false;
                child.props.onBlur?.(event);
            }
        };
        const triggerProps = {
            ...child.props,
            tabIndex: child.props.tabIndex ?? 0,
            className: cx('trigger-element', child.props.className, { active: visible, disabled }),
            onClick: handleClickTrigger,
            onFocus: handleFocusBlurTrigger,
            onBlur: handleFocusBlurTrigger,
        };
        triggerElement = React.cloneElement(child, triggerProps);
    }
    const activeOptions = React.useMemo(() => (innerOptions ? innerOptions.filter((option) => !option.disabled) : []), [innerOptions]);
    const isActive = React.useCallback((value) => {
        if (innerValue === undefined) {
            return false;
        }
        if (isMultiple && Array.isArray(innerValue)) {
            return innerValue.includes(value);
        }
        return innerValue === value;
    }, [innerValue, isMultiple]);
    const setValue = React.useCallback((value) => {
        if (isMultiple && value !== undefined) {
            const pos = (innerValue || []).indexOf(value);
            const data = [...(innerValue || [])];
            pos >= 0 ? data.splice(pos, 1) : data.push(value);
            setInnerValue(data, (val) => {
                val && onChange?.(val);
            });
            return;
        }
        setInnerValue(value, (val) => {
            isDefined(val) && onChange?.(val);
        });
    }, [innerValue, isMultiple, onChange, setInnerValue]);
    const closeMenuOnSelect = React.useCallback((value) => (isDefined(closeOnSelect) && isFunction(closeOnSelect) ? closeOnSelect(value) : closeOnSelect), [closeOnSelect]);
    const handleSelect = React.useCallback((value, event) => {
        setValue(value);
        event && onClick?.(event);
        !isMultiple && closeMenuOnSelect(value) && setVisible.off();
    }, [setValue, onClick, isMultiple, closeMenuOnSelect, setVisible]);
    const getCurrentIndex = React.useCallback((value) => {
        if (!Array.isArray(innerOptions) || !value)
            return -1;
        if (isMultiple && Array.isArray(value)) {
            return activeOptions.findIndex((option) => option.value === value[value.length - 1]);
        }
        return activeOptions.findIndex((option) => option.value === value);
    }, [innerOptions, activeOptions, isMultiple]);
    const handleMouseMove = React.useCallback(() => {
        setFocusedIndex(getCurrentIndex(innerValue));
    }, [innerValue, getCurrentIndex]);
    const sortOptions = React.useCallback(() => {
        if (innerValue == null || !options)
            return;
        if (Array.isArray(innerValue)) {
            const selectedOptions = innerValue
                .map((value) => options.find((option) => option.value === value))
                .filter((option) => option !== undefined);
            setInnerOptions(Array.from(new Set([...selectedOptions, ...options])));
            return;
        }
        const sortedOptions = [...options].sort((item) => {
            if (item.value !== innerValue)
                return 1;
            return -1;
        });
        setInnerOptions(sortedOptions);
    }, [innerValue, options]);
    const getFocusedValue = React.useCallback(() => {
        const item = activeOptions[focusedIndex];
        return item ? item.value : null;
    }, [activeOptions, focusedIndex]);
    const menu = React.useMemo(() => (React.createElement(Menu, { className: cx('menu') }, innerOptions.map(({ label, value, disabled, ...args }) => (React.createElement(Menu.Item, { ...args, key: value, value: value, disabled: disabled, active: isActive(value) || getFocusedValue() === value, onClick: disabled ? noop : (event) => handleSelect(value, event), onMouseOver: handleMouseMove }, label))))), [cx, innerOptions, isActive, getFocusedValue, handleMouseMove, handleSelect]);
    const content = React.useMemo(() => {
        if (innerOptions.length)
            return menu;
        return isDefined(emptyContent) ? (emptyContent) : (React.createElement(Menu, null,
            React.createElement(Menu.Item, { disabled: true },
                React.createElement(Text, { className: "h-text-center h-color-D60" }, defaultEmptyContentText))));
    }, [innerOptions, emptyContent, menu]);
    const defaultFooter = React.useMemo(() => (React.createElement(Button, { type: "button", color: "blue", variant: "primary", onClick: setVisible.off }, "\u0413\u043E\u0442\u043E\u0432\u043E")), [setVisible.off]);
    useKeyPress({
        enabled: visible || focused.current,
        keyMap: {
            ArrowDown: (event) => {
                event.preventDefault();
                if (!visible) {
                    setVisible.on();
                    return;
                }
                const nextIndex = getNextIndex(focusedIndex, activeOptions.length);
                setFocusedIndex(nextIndex);
            },
            ArrowUp: (event) => {
                event.preventDefault();
                if (!visible) {
                    setVisible.on();
                    return;
                }
                const prevIndex = getPrevIndex(focusedIndex, activeOptions.length);
                setFocusedIndex(prevIndex);
            },
            Enter: (event) => {
                event.preventDefault();
                const value = getFocusedValue();
                if (visible && value !== undefined && value !== null) {
                    handleSelect(value);
                }
            },
            Escape: (event) => {
                event.preventDefault();
                setVisible.off();
            },
            Tab: () => {
                setVisible.off();
                focused.current = false;
            },
        },
    }, [innerValue, focusedIndex, activeOptions, visible, isMultiple, closeOnSelect]);
    React.useEffect(() => {
        if (visible) {
            onOpen?.();
        }
        else {
            !isFirstRender.current && onClose?.();
        }
    }, [visible, onClose, onOpen]);
    React.useEffect(() => {
        if (isDefined(value)) {
            setInnerValue(value);
        }
        else {
            setFocusedIndex(-1);
            !isFirstRender.current && setInnerValue(undefined);
        }
        isFirstRender.current = false;
    }, [value, setInnerValue]);
    React.useEffect(() => {
        if (visible) {
            setFocusedIndex(getCurrentIndex(innerValue));
        }
    }, [visible, innerValue, getCurrentIndex]);
    React.useEffect(() => {
        setInnerOptions(options || []);
    }, [options]);
    React.useEffect(() => {
        optionsSort && !visible && sortOptions();
    }, [visible, optionsSort, sortOptions]);
    return (React.createElement(Popover, { "data-qa": "Dropdown", ref: ref, className: popoverClassNames, visible: visible, header: mobile ? header : null, content: content, footer: mobile ? defaultFooter : null, mobile: mobile, positionerSettings: makePositionerSettings(placement, matchWidth, flip), onClose: setVisible.off, fullscreen: fullscreen, ...restProps }, triggerElement));
});
Dropdown.defaultProps = {
    placement: 'bottom-start',
    matchWidth: false,
    optionsSort: true,
    closeOnSelect: true,
    fullscreen: true,
};
Dropdown.displayName = 'Dropdown';

export { Dropdown, defaultEmptyContentText };
//# sourceMappingURL=Dropdown.js.map
